import { Flame, Signal, SignalLow, TriangleAlert } from 'lucide-react';
import { summarizeOpticalPower, type Onu } from '@simpleacs/shared';
import { Card, Pill } from '../../components/ui';

/** What the ONU list's signal filter can be set to from here. */
export type SignalFilterValue = 'all' | 'high' | 'normal' | 'low' | 'unknown';

/**
 * The subscribers whose light is wrong, on the page where you would act on it.
 *
 * Two failures, not one. Too LITTLE light is the one everybody looks for — a
 * dirty connector, a bend, a splitter too far down a cascade. Too MUCH is the
 * one that gets missed: a subscriber spliced close to the OLT with no
 * attenuation overdrives the receiver, and the link degrades in a way that
 * reads like a flapping ONU rather than an optical problem. The card gives
 * them equal billing for that reason.
 *
 * It counts through `summarizeOpticalPower`, which counts through
 * `signalLevel` — the same function behind every coloured pill in the table
 * below it. Three copies of "-8 dBm" is how a card comes to report nine
 * problems the list cannot show.
 *
 * Every number is a filter. A count an operator cannot click is a count they
 * have to reproduce by hand in the controls underneath, which is the whole
 * distance between a dashboard and a tool.
 *
 * The unmeasured are shown and never folded into the healthy number. On this
 * fleet a chassis read can fail quietly, so "42 not measured" is a finding
 * about the platform, not a clean bill of health for 42 subscribers.
 */
export function OpticalPowerCard({
  onus,
  scope,
  value,
  onChange,
}: {
  /** The rows the page has loaded — the OLT's inventory, or the fleet's. */
  onus: ReadonlyArray<Pick<Onu, 'rxPowerDbm'>>;
  /** What those rows are: a chassis name, or the fleet. */
  scope: string;
  value: SignalFilterValue;
  onChange: (next: SignalFilterValue) => void;
}) {
  const summary = summarizeOpticalPower(onus);
  const troubled = summary.high + summary.low;

  /** One clickable count. Selected twice means "stop filtering". */
  const Band = ({
    filter,
    label,
    count,
    hint,
    tone,
    icon,
  }: {
    filter: SignalFilterValue;
    label: string;
    count: number;
    hint: string;
    tone: 'danger' | 'warn' | 'success' | 'neutral';
    icon: React.ReactNode;
  }) => {
    const active = value === filter;
    return (
      <button
        type="button"
        onClick={() => onChange(active ? 'all' : filter)}
        aria-pressed={active}
        className={`flex flex-col gap-1 rounded-lg border p-3 text-left transition ${
          active ? 'border-primary bg-primary-soft' : 'border-line hover:border-primary/40'
        }`}
      >
        <span className="flex items-center gap-1.5 text-xs text-muted">
          {icon}
          {label}
        </span>
        <span className="flex items-baseline gap-2">
          <span className="text-xl font-semibold tabular-nums text-ink">{count}</span>
          {count > 0 && tone !== 'neutral' && <Pill state tone={tone}>{hint}</Pill>}
        </span>
        {count === 0 && <span className="text-xs text-faint">{hint}</span>}
      </button>
    );
  };

  return (
    <Card
      className="mt-5"
      title={
        <span className="flex items-center gap-2">
          <Signal className="size-4 text-muted" />
          Optical power
          {troubled > 0 && (
            <Pill tone="warn">
              {troubled} to look at
            </Pill>
          )}
        </span>
      }
    >
      <div className="space-y-3 p-4">
        <p className="text-xs text-muted">
          Receive levels across {scope}. Too much light damages a link as surely as too little —
          pick a band to filter the list below it.
        </p>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Band
            filter="high"
            label="Too much light"
            count={summary.high}
            tone="warn"
            hint={
              summary.high > 0 && summary.highestDbm !== null
                ? `strongest ${summary.highestDbm} dBm`
                : 'above −8 dBm'
            }
            icon={<Flame className="size-3.5" />}
          />
          <Band
            filter="low"
            label="Too little light"
            count={summary.low}
            tone="danger"
            hint={
              summary.low > 0 && summary.lowestDbm !== null
                ? `weakest ${summary.lowestDbm} dBm`
                : 'below −25 dBm'
            }
            icon={<SignalLow className="size-3.5" />}
          />
          <Band
            filter="normal"
            label="In range"
            count={summary.normal}
            tone="success"
            hint="−8 to −25 dBm"
            icon={<Signal className="size-3.5" />}
          />
          <Band
            filter="unknown"
            label="Not measured"
            count={summary.unknown}
            tone="neutral"
            hint={
              summary.unknown > 0
                ? 'nothing has read these yet'
                : 'every ONU here has a reading'
            }
            icon={<TriangleAlert className="size-3.5" />}
          />
        </div>

        {summary.total === 0 && (
          <p className="text-xs text-faint">No ONUs in this scope to measure.</p>
        )}
      </div>
    </Card>
  );
}
